import { useQuery } from "@apollo/client/react";
import React, { useState } from "react";
import {
  ArrowLeftIcon,
  FingerprintPatternIcon,
  SparklesIcon,
} from "lucide-react";
import { StoreApi, UseBoundStore } from "zustand";
import { useShallow } from "zustand/react/shallow";

import { NodeStore } from "@/components/@types";
import { FieldTypography } from "@/components/organisms/Field/FieldTypography";
import { useNodeField } from "@/components/organisms/Node/Node.hooks";
import { NodeActionsPopper } from "@/components/organisms/Node/NodeActionsPopper";
import { Mutations, Queries } from "@/constants";
import {
  NodeTitleLayoutEntityBox,
  type NodeTypeIconChange,
} from "@/components/organisms/Node/NodeTitleLayoutEntityBox";
import { useContextHotkeys, useContextScope } from "@/hooks/useContextTree";
import { useDevice } from "@/hooks/useDevice";
import { ObjectActionType } from "@/objects/@types";
import { State } from "@/state";
import { setCopiedReference } from "@/utils/clipboardReference";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography, typographyStyles } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

/**
 * Renders the title as an editable field when the object definition
 * specifies a `titleFieldKey` and the field is editable.
 * Falls back to null so the parent can render a static title.
 */
export function NodeTitleEditable({
  store,
  isLoading,
  fallbackTitle,
  size = "md",
  layout = "fill",
  titleLevel,
}: {
  store: UseBoundStore<StoreApi<NodeStore>>;
  isLoading: boolean;
  fallbackTitle: string;
  size?: "sm" | "md";
  /** ``content`` sizes to the title text (e.g. app topbar breadcrumbs). */
  layout?: "fill" | "content";
  /** Overrides the level ``size`` and ``layout`` would pick. */
  titleLevel?: "body-md" | "title-md" | "title-lg";
}) {
  const objectType = store(useShallow((state) => state.objectType));
  const titleFieldKey = (objectType?.titleFieldKey as string) || "";

  // Own context with capturesInput so single-key hotkeys (e.g. "l", "f")
  // from ancestor/global scopes are suppressed while typing in the title.
  const { Scope } = useContextScope({
    capturesInput: true,
  });

  const { fieldValues, fieldEvents } = useNodeField(
    store,
    titleFieldKey || "__noop__",
  );
  const titleFieldDefinition = store((state) =>
    state.fieldDefinitions.find((field) => field.objectKey === titleFieldKey),
  );
  const isRevealingMissingRequired = store(
    (state) => state.missingRequiredRevealCount > 0,
  );
  const isCreating = store((state) => state.mode === "create");
  // A title that is not the user's to edit (an institution's name, which comes
  // from its bank) is still the record's title, read from the record rather
  // than the identifier a caller falls back on.
  const recordTitle = store(
    (state) =>
      objectType?.getTitle?.(state.fieldValues as never) ||
      ((state.fieldValues as Record<string, unknown>)?.[titleFieldKey] as
        | string
        | undefined) ||
      "",
  );

  const isContentWidth = layout === "content";
  // ``content`` is the topbar breadcrumb: plain body text, like the crumbs
  // beside it. ``fill`` is a panel or page header, which keeps the title scale.
  const typographyLevel =
    titleLevel ??
    (isContentWidth ? "body-md" : size === "sm" ? "title-md" : "title-lg");

  // If no titleFieldKey or field isn't editable, render static title
  if (!titleFieldKey || !fieldEvents) {
    return (
      <Typography
        level={typographyLevel}
        className={cn(
          "truncate",
          "whitespace-nowrap",
          isContentWidth ? "w-fit" : "w-full",
          "max-w-full min-w-0 overflow-hidden text-ellipsis",
        )}
      >
        <Skeleton loading={isLoading}>
          {recordTitle || fallbackTitle || "No title..."}
        </Skeleton>
      </Typography>
    );
  }

  const value = (fieldValues[titleFieldKey] as string) || "";
  const isTitleRequired =
    isCreating && titleFieldDefinition?.isRequired === true;
  const isTitleMissing = isTitleRequired && value.trim() === "";

  return (
    <Scope>
      <div
        data-missing-required={isTitleMissing ? "true" : undefined}
        className={cn(
          isContentWidth ? "w-fit" : "w-full",
          isContentWidth ? "grow-0" : "grow-2",
          "shrink min-w-0 max-w-full overflow-hidden",
          size === "sm" ? "p-1" : "p-1.5",
          "rounded-sm flex items-baseline gap-1 hover:bg-secondary",
          isRevealingMissingRequired &&
            isTitleMissing &&
            "[outline:1px_solid] outline-destructive/50",
        )}
      >
        <FieldTypography
          value={value}
          events={fieldEvents}
          valueOptions={{
            // A title the record needs says so while it is empty; an optional
            // one keeps the plain "No title...".
            placeholder: isTitleRequired
              ? `Name this ${objectType?.humanName ?? "record"}`
              : fallbackTitle || "No title...",
          }}
          size={size}
          isLoading={isLoading}
          style={{
            ...((typographyStyles as Record<string, React.CSSProperties>)[
              typographyLevel
            ] as React.CSSProperties),
            whiteSpace: "nowrap",
            width: isContentWidth ? "fit-content" : "100%",
            maxWidth: "100%",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        />
        {isTitleRequired && (
          <Typography
            level={typographyLevel}
            aria-label="required"
            data-testid="required-asterisk"
            className={cn(
              "shrink-0",
              isTitleMissing ? "text-destructive" : "text-muted-foreground/80",
            )}
          >
            *
          </Typography>
        )}
      </div>
    </Scope>
  );
}

/**
 * Copies the record's identifier, and keeps it as the reference a chat's
 * composer pastes - from its actions menu.
 */
function copyNodeReference(
  store: UseBoundStore<StoreApi<NodeStore>>,
  identifier: string | undefined,
) {
  if (!identifier) return;
  navigator.clipboard.writeText(identifier);
  const { fieldValues: fv, objectType } = store.getState();
  const displayName =
    (fv.name as string) ||
    (objectType?.getTitle(fv as Parameters<typeof objectType.getTitle>[0]) as
      | string
      | undefined) ||
    "";
  setCopiedReference({
    id: fv.id as string,
    identifier,
    name: displayName,
    typename: (fv.__typename as string) || "",
  });
  State.setState({ snackbarMessage: "Copied reference" });
}

interface NodeTitleLayoutProps {
  title: string;
  mode?: "create" | "update";
  isLoading?: boolean;
  options?: React.ReactNode[];
  store: UseBoundStore<StoreApi<NodeStore>> | null;
  className?: string;
  /** Size variant for spacing and typography. Default: "md" */
  size?: "sm" | "md";
  /**
   * Title typography, when it should not follow ``size`` — ``body-md`` for
   * the record cards in the thread, which read as body text.
   */
  titleLevel?: "body-md" | "title-md" | "title-lg";
  /** Optional identifier override - if not provided, fetched from store */
  identifier?: string;
  /** Content rendered before the entity box (e.g. expand chevron) */
  startDecorator?: React.ReactNode;
  /** Content rendered after the identifier / options area */
  endDecorator?: React.ReactNode;
  /** Content rendered after the actions menu. */
  trailingDecorator?: React.ReactNode;
  /**
   * What puts the bar's panel away: at the bar's end on a desktop, and before
   * the title on a phone, where the bar is the screen's navigation bar and
   * the way back is at its top left.
   */
  closeButton?: React.ReactNode;
  /** Object actions menu (rendered in the title bar). */
  actions?: ObjectActionType[];
  /** Click handler for the entire title bar */
  onClick?: () => void;
  /** Hide entity chip + title (e.g. when the app topbar breadcrumb shows the title). */
  suppressTitle?: boolean;
  /** Show the record's type icon beside its entity box. */
  isTypeIconVisible?: boolean;
  /** A change to the record, said on its type icon. */
  typeIconChange?: NodeTypeIconChange | null;
  /** The record page's own tools - its search index status, its ID to copy - left off: the record is only named. */
  hideRecordTools?: boolean;
  /** The business the record is shown in: one of its own needs no badge. */
  contextEntityIdentifier?: string | null;
}

export function NodeTitleLayout({
  title,
  mode = "update",
  store,
  isLoading = false,
  options = [],
  className,
  size = "md",
  titleLevel,
  identifier,
  startDecorator,
  endDecorator,
  trailingDecorator,
  closeButton,
  actions,
  onClick,
  suppressTitle = false,
  isTypeIconVisible = false,
  typeIconChange = null,
  hideRecordTools = false,
  contextEntityIdentifier = null,
}: NodeTitleLayoutProps) {
  const queuedPanelNodeIds =
    mode === "update" ? State((store) => store.queuedPanelNodeIds) : [];
  const popNodeIdFromQueue = State((store) => store.popNodeIdFromQueue);
  const [isActionsPopperOpen, setIsActionsPopperOpen] = useState(false);

  useContextHotkeys({
    id: "node-title-actions-open",
    combos: "meta+j",
    callback: () => setIsActionsPopperOpen(true),
    isEnabled: Boolean(actions && actions.length > 0),
  });

  const typographyLevel =
    titleLevel ?? (size === "sm" ? "title-md" : "title-lg");

  // A panel's bar on a phone - one that puts its panel away - is the screen's
  // navigation bar: the way back, the business's box and the title as a
  // desktop has them, and one menu. A window as narrow on a computer, the
  // panel over the page there too, has the same bar.
  const { isNarrow } = useDevice();
  const isNavigationBar = isNarrow && closeButton != null;
  const showsRecordTools = Boolean(store) && !hideRecordTools;
  // The record's own tools are in its actions menu, after its actions: its ID
  // to copy, and whether AI can find it by meaning - the embedding behind
  // semantic search is a billed call development leaves off, so a row can
  // sit in the index findable by its words alone. Picked then, it is read
  // into the index again (and on a laptop honestly stays so).
  const nodeId = store?.getState().fieldValues.id as string | undefined;
  const { data: embedding } = useQuery(Queries.NODE_EMBEDDING_STATE, {
    variables: { id: nodeId as string },
    skip: !showsRecordTools || !nodeId || isLoading,
    fetchPolicy: "cache-first",
  });
  const isEmbedded = (embedding?.node as { isEmbedded?: boolean } | undefined)
    ?.isEmbedded;
  const menuActions: ObjectActionType[] | undefined =
    store && showsRecordTools
      ? [
          ...(actions ?? []),
          {
            label: "Copy ID",
            icon: FingerprintPatternIcon,
            isIconButton: false,
            onClick: () =>
              copyNodeReference(
                store,
                identifier ??
                  ((store.getState().fieldValues as Record<string, unknown>)
                    ?.identifier as string | undefined),
              ),
          },
          ...(nodeId && isEmbedded !== undefined
            ? [
                isEmbedded
                  ? {
                      label: "AI can find this by meaning",
                      icon: SparklesIcon,
                      isIconButton: false,
                    }
                  : {
                      label: "Let AI find this by meaning",
                      icon: SparklesIcon,
                      isIconButton: false,
                      mutationOptions: {
                        mutation: Mutations.REINDEX_OBJECT,
                        options: {
                          variables: { input: { id: nodeId } },
                          refetchQueries: [
                            {
                              query: Queries.NODE_EMBEDDING_STATE,
                              variables: { id: nodeId },
                            },
                          ],
                        },
                      },
                    },
              ]
            : []),
        ]
      : actions;

  return (
    <div
      // Padding a caller adds stays inside that width rather than pushing
      // the trailing controls past the edge of whatever holds the bar.
      className={cn(
        "z-1 bg-background flex items-center justify-center w-full box-border",
        size === "sm" ? "min-h-9" : "min-h-11.75",
        className,
      )}
    >
      <div
        data-testid="node-title-bar"
        className={cn(
          "flex min-w-0 flex-wrap",
          "flex items-center justify-start flex-row flex-nowrap",
          queuedPanelNodeIds.length > 0
            ? "w-[calc(100%+8px)]"
            : "w-[calc(100%+0px)]",
          queuedPanelNodeIds.length > 0
            ? "max-w-[calc(100%+8px)]"
            : "max-w-[calc(100%+0px)]",
          queuedPanelNodeIds.length > 0 ? "-ml-2" : "ml-0",
          size === "sm" ? "gap-1.5" : "gap-2",
        )}
      >
        {isNavigationBar && closeButton}
        {startDecorator && (
          <div
            onClick={onClick}
            className={cn("flex items-center", onClick && "cursor-pointer")}
          >
            {startDecorator}
          </div>
        )}
        {queuedPanelNodeIds.length > 0 && (
          <Button
            variant="outline"
            size={size === "sm" ? "icon-xs" : "icon-sm"}
            className={cn(size === "sm" ? "mr-1" : "mr-2")}
          >
            <ArrowLeftIcon
              onClick={() => {
                popNodeIdFromQueue();
              }}
            />
          </Button>
        )}
        {store && !suppressTitle && (
          <NodeTitleLayoutEntityBox
            store={store}
            size={size}
            isLoading={isLoading}
            isTypeIconVisible={isTypeIconVisible}
            typeIconChange={typeIconChange}
            contextEntityIdentifier={contextEntityIdentifier}
          />
        )}
        {!suppressTitle && (
          <div className="min-w-0 grow-3 overflow-hidden">
            {store ? (
              <NodeTitleEditable
                store={store}
                isLoading={isLoading}
                fallbackTitle={title}
                size={size}
                titleLevel={titleLevel}
              />
            ) : (
              <Typography
                level={typographyLevel}
                className="truncate overflow-hidden text-ellipsis whitespace-nowrap max-w-full ml-0"
              >
                <Skeleton loading={isLoading}>
                  {title || "No title..."}
                </Skeleton>
              </Typography>
            )}
          </div>
        )}
        <div className="grow" />
        <div className="flex flex-row items-center gap-0.5">
          {options && options.length > 0 && (
            <>
              {options.map((option, index) => (
                <div key={index}>{option}</div>
              ))}
            </>
          )}
          {endDecorator}
          {menuActions && menuActions.length > 0 && (
            <NodeActionsPopper
              actions={menuActions}
              isOpenState={[isActionsPopperOpen, setIsActionsPopperOpen]}
            />
          )}
          {!isNavigationBar && closeButton}
          {trailingDecorator}
        </div>
      </div>
    </div>
  );
}
